import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  listPipelines,
  createPipeline,
  deletePipeline,
} from '../services/pipelineService';

export function usePipelines() {
  const { user, isAuthenticated } = useAuth();
  const [pipelines, setPipelines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setPipelines([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const data = await listPipelines();
      setPipelines(data);
    } catch (err) {
      const msg = err.message || 'Failed to load pipelines';
      if (/pipelines/i.test(msg) && /does not exist|schema cache/i.test(msg)) {
        setError(
          'Pipelines table not found. Run database/migrations/005_create_pipelines.sql in the Supabase SQL editor.'
        );
      } else {
        setError(msg);
      }
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const create = useCallback(async (name) => {
    const pipeline = await createPipeline({ name });
    setPipelines((prev) => [pipeline, ...prev]);
    return pipeline;
  }, []);

  const remove = useCallback(async (id) => {
    await deletePipeline(id);
    setPipelines((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return { pipelines, isLoading, error, refetch, create, remove };
}
